import {
  DateColumn,
  EnhancedDetailsList,
  OtherColumn,
  SelectColumn,
  UserDtoColumn
} from '../../common/EnhancedDetailsList';
import {
  ColumnKey,
  IEnhancedColumn,
  SpecialColumnKey
} from '../../common/EnhancedDetailsList/IEnhancedDetailsList';
import {HeadingWithDecoration} from '../../common/HeadingWithDecoration';
import {Label, Shimmer, Spinner} from '@fluentui/react';
import {MiddleTopBarUnit} from '../Unit/MiddleTopBarUnit';
import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {Helmet} from 'react-helmet';
import * as useUnitFromUnit from '../Unit/useUnit';
import {CabinetTypical} from '../../../models/CabinetTypical';
import {useCabinetTypicals} from '../../../hooks/services/useCabinetTypicals';
import {OpenPDCCabinetColumn} from '../../common/EnhancedDetailsList/ColumnTypes/OpenPDCCabinetColumn';
import {TheButton} from '../../common/TheButton';
import {ModalWithHeader} from '../../common/ModalWithHeader';
import {EditCabinetTypical} from './EditCabinetTypical';
import {useService} from '../../../hooks';
import {checkPermissions} from '../../../helpers/permissionsHtml';
import {actions, PermissionToCheck} from '../../../enums';
import {InfoTooltip} from '../../common/InfoTooltip';
import styles from './CabinetTypicals.module.scss';
import {PeoplePicker} from '../../common/PeoplePicker';
import {useCabinetTypical} from './useCabinetTypical';
import { GlobalSettingsContext } from '../../../providers/GlobalSettingsContext';

export const CabinetTypicals: React.FunctionComponent = () => {
  const {unitId, apiMsg, publishedProcessesForUnit, unitPermissions} = useUnitFromUnit.useUnit();

  const {
    isLoadingInProgress,
    isUserAdmin,
    manageCabTypRolesModal,
    onManageCabTypRoles,
    setManageCabTypRolesModal,
    cabinetEditorsSelect,
    onSelectCabinetEdtitors,
    saveForm
  } = useCabinetTypical();

  const {getCabinetTypicals} = useCabinetTypicals();
  const {getUserPermissionsPage} = useService();
  const {globalSettings} = useContext(GlobalSettingsContext);
  const cabinetTypicalHelpAndManual = globalSettings.settings.cabinetTypicalHelpAndManual;

  
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [cabinetTypicals, setCabinetTypicals] = useState<CabinetTypical[]>([]);
  const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
  const [editedId, setEditedId] = useState<number>(0);
  const [title, setTitle] = useState<string | null>(null);
  const [canEditCabinets, setCanEditCabinets] = useState<boolean>(false);
  
 

  const onEditDetailsClick = useCallback((item: CabinetTypical) => {
    setEditedId(item.id);
    setTitle(item.typicalId);
    setIsEditModalVisible(true);
  }, []);

  const refreshDocuments = useMemo(
    () => async () => {
      setIsLoading(true);
      const cabinetTypicalsResponse = await getCabinetTypicals();
      if (cabinetTypicalsResponse.result) {
        setCabinetTypicals(cabinetTypicalsResponse.result);
      }
      setIsLoading(false);
    },
    [getCabinetTypicals, setCabinetTypicals]
  );

  const onCloseModal = () => {
    setIsEditModalVisible(false);
  };

  const onCloseManageRoleModal = () => {
    setManageCabTypRolesModal(false);
  };

  useEffect(() => {
    (async () => {
      const response = await getUserPermissionsPage(unitId);
      if (response?.result) {
        const canEdit = checkPermissions(
          PermissionToCheck.EditCabinetTypicals,
          actions.UPDATE,
          response.result
        );
        setCanEditCabinets(canEdit);
      }
    })();
  }, [getUserPermissionsPage, setCanEditCabinets, unitId]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      refreshDocuments();
      setIsLoading(false);
    })();
  }, [getCabinetTypicals, refreshDocuments]);

  const isSmart = useMemo(
    () => typeof new URLSearchParams(document.location.search).get('smart') === 'string',
    []
  );
  const columns = useMemo(() => {
    const cols: IEnhancedColumn<CabinetTypical>[] = [
      {
        key: ColumnKey.Url,
        name: '',
        data: OpenPDCCabinetColumn
      },
      ...(isSmart
        ? ([
            {
              key: 'id',
              name: 'ID',
              minWidth: 130
            }
          ] as IEnhancedColumn<CabinetTypical>[])
        : []),
      {
        key: ColumnKey.TypicalId,
        name: 'Typical Id',
        minWidth: 215,
        flexGrow: 1,
        exportToExcel: true,
        onExportToExcel: (item, column) => {
          return {
            text: item.typicalId,
            link: item.url
          };
        }
      },
            {
        key: ColumnKey.TypicalImsId,
        name: 'Typical IMS Id',
        minWidth: 150,
        exportToExcel: true
      },
      ...(canEditCabinets
        ? ([
            {
              key: SpecialColumnKey.Edit,
              name: 'Edit details',
              onRender: (item: CabinetTypical) => {
                return (
                  <TheButton
                    iconProps={{
                      iconName: 'Edit'
                    }}
                    onClick={() => onEditDetailsClick(item)}
                    disabled={false}
                    primary>
                    Edit details
                  </TheButton>
                );
              },
              data: OtherColumn,
              minWidth: 200
            }
          ] as IEnhancedColumn<CabinetTypical>[])
        : []),
        {
        key: ColumnKey.Created,
        name: 'Date of Insert to PDC',
        data: DateColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.NativeTool,
        name: 'Native Tool',
        data: SelectColumn,
        minWidth: 135,
        exportToExcel: true
      },
      {
        key: ColumnKey.Responsibility,
        name: 'Responsibility',
        minWidth: 150,
        data: SelectColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.CabinetType,
        name: 'Cabinet Type',
        data: SelectColumn,
        minWidth: 215,
        exportToExcel: true
      },
       {
        key: ColumnKey.Height,
        name: 'Height',
        minWidth: 105,
        data: SelectColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.Width,
        name: 'Width',
        minWidth: 105,
        data: SelectColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.Depth,
        name: 'Depth',
        minWidth: 105,
        data: SelectColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.Plinth,
        name: 'Plinth',
        minWidth: 105,
        data: SelectColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.CableEntry,
        name: 'Cable Entry',
        data: SelectColumn,
        minWidth: 135,
        exportToExcel: true
      },
      {
        key: ColumnKey.CabinetAccess,
        name: 'Cabinet Access',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true,
      },
      {
        key: ColumnKey.CabinetSwingFrame,
        name: 'Cabinet Swing Frame',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.EquipmentOnDoor,
        name: 'Equipment On Door',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.ControlSystem,
        name: 'Control System',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.System,
        name: 'System',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.NumberOfControllers,
        name: 'Number Of Controllers',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },

      {
        key: ColumnKey.CpuRedundancy,
        name: 'CPU Redundancy',
        data: SelectColumn,
        minWidth: 115,
        exportToExcel: true
      },
      {
        key: ColumnKey.PowerInput,
        name: 'Power Input',
        data: SelectColumn,
        minWidth: 135,
        exportToExcel: true
      },
      {
        key: ColumnKey.PowerSupplyRedundancy,
        name: 'Power Supply Redundancy',
        data: SelectColumn,
        minWidth: 115,
        exportToExcel: true
      },
      {
        key: ColumnKey.IoRedundancy,
        name: 'IO Redundancy',
        data: SelectColumn,
        minWidth: 115,
        exportToExcel: true
      },
      {
        key: ColumnKey.IsIOSignals,
        name: 'Is IO Signals',
        data: SelectColumn,
        minWidth: 115,
        exportToExcel: true
      },
      {
        key: ColumnKey.IoSignalsCount,
        name: 'IO Signals Count',
        data: SelectColumn,
        minWidth: 115,
        exportToExcel: true
      },
      {
        key: ColumnKey.ForExAreas,
        name: 'For Ex Areas Type',
        data: SelectColumn,
        minWidth: 150,
        exportToExcel: true
      },
      {
        key: ColumnKey.Ip,
        name: 'IP>=55',
        data: SelectColumn,
        minWidth: 105,
        exportToExcel: true
      },
      {
        key: ColumnKey.ProjectName,
        name: 'Project Name',
        minWidth: 255,
        exportToExcel: true
      },
      {
        key: ColumnKey.AccessToSourcesTitle,
        name: 'Access To Sources',
        data: SelectColumn,
        minWidth: 215,
        exportToExcel: true,
         onRender(item) {
          return (
            <a
              href={item.accessToSourcesUrl && item.accessToSourcesUrl.startsWith("http") ? item.accessToSourcesUrl : `https://${item.accessToSourcesUrl}`}
              target="_blank"
              rel="noreferrer"
              className={styles.link}
            >{item.accessToSourcesTitle}
            </a>
          );
        },
          onExportToExcel: (item, column) => {
          return {
            text: item.accessToSourcesTitle,
            link: item.accessToSourcesUrl && item.accessToSourcesUrl.startsWith("http") ? item.accessToSourcesUrl : `https://${item.accessToSourcesUrl}`
          };
        }
      },
      {
        key: ColumnKey.Comment,
        name: 'Comment',
        minWidth: 215,
        exportToExcel: true
      },
      {
        key: ColumnKey.Industry,
        name: 'Industry',
        data: SelectColumn,
        minWidth: 105,
        exportToExcel: true
      },
      {
        key: ColumnKey.ManufacturedBy,
        name: 'Manufactured By',
        minWidth: 175,
        data: SelectColumn,
        exportToExcel: true
      },
      {
        key: ColumnKey.Approver,
        name: 'Approver',
        minWidth: 160,
        data: UserDtoColumn,
        exportToExcel: true
      },
       {
        key: ColumnKey.PreparedBy,
        name: 'Prepared By',
        minWidth: 160,
        data: UserDtoColumn,
        exportToExcel: true
      },
       {
        key: ColumnKey.DesigneRespPerson,
        name: 'Design Responsible Person',
        minWidth: 215,
        data: UserDtoColumn,
        exportToExcel: true
      },
       {
        key: ColumnKey.DocStatus,
        name: 'Status',
        minWidth: 150,
        data: SelectColumn,
        exportToExcel: true
      }
    ];
    return cols;
  }, [isSmart, onEditDetailsClick, canEditCabinets]);

  return (
    <>
      <main className={`${isLoading ? 'isLoading' : ''} noPaddingTop`}>
        {
          <Helmet>
            <title>IMS Cabinet Typicals List</title>
          </Helmet>
        }
        {isLoading ? (
          <Spinner />
        ) : (
          !apiMsg && (
            <>
              {publishedProcessesForUnit ? (
                <MiddleTopBarUnit
                  unitId={unitId}
                  publishedData={publishedProcessesForUnit?.result}
                  unitPermissions={unitPermissions}
                  showManage
                />
              ) : (
                <Shimmer />
              )}
              {<HeadingWithDecoration text={'Cabinet Typicals List'} />}
              <EnhancedDetailsList<CabinetTypical, CabinetTypical, CabinetTypical>
                columns={columns}
                items={cabinetTypicals}
                itemsFiltered={false}
                pageSize={30}
                clearFilters={true}
                manageCabTypRoles={isUserAdmin}
                cabinetTypicalHelpAndManual={cabinetTypicalHelpAndManual}
                onManageCabinetTypicalRoles={onManageCabTypRoles}
                excelExport
                excelFileName={'Exported Cabinet Typicals List'}
              />
              {isEditModalVisible && (
                <ModalWithHeader
                  header={`Edit: ${title}` || 'Edit Cabinet Typical'}
                  isVisible={isEditModalVisible}
                  dismiss={onCloseModal}>
                  <EditCabinetTypical
                    cabinetTypicalId={editedId}
                    setIsEditModalVisible={setIsEditModalVisible}
                    refreshDocuments={refreshDocuments}
                  />
                </ModalWithHeader>
              )}
              {manageCabTypRolesModal && (
                <ModalWithHeader
                  header={'Edit Cabinet Typical Editor Roles'}
                  isVisible={manageCabTypRolesModal}
                  dismiss={onCloseManageRoleModal}>
                  {isLoadingInProgress ? (
                    <Spinner />
                  ) : (
                    <>
                      <div className={styles.labelWrapper}>
                        <Label className={styles.label} htmlFor={'people'}>
                          Cabinet Typicals Editors
                        </Label>
                        <InfoTooltip content="Cabinet Editors have full access to Edit Cabinet Typicals Properties" />
                      </div>
                      <PeoplePicker
                        key={'normal'}
                        itemLimit={30}
                        defaultSelectedPerson={cabinetEditorsSelect}
                        onSelectPerson={onSelectCabinetEdtitors}
                        showRemoved
                      />
                    </>
                  )}
                  <div className={styles.footer}>
                    <TheButton onClick={onCloseManageRoleModal}>Cancel</TheButton>
                    <TheButton disabled={isLoadingInProgress} primary onClick={saveForm}>
                      Save
                    </TheButton>
                  </div>
                </ModalWithHeader>
              )}
            </>
          )
        )}
      </main>
    </>
  );
};
